'use client';

import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, ImageIcon } from 'lucide-react';

interface CollectionData {
  id: string;
  name: string;
  count: number;
  walls: number;
  singles: number;
  equivalentCount: number;
  images: string[];
}

const COLLECTION_DESC: Record<string, { tagline: string; description: string }> = {
  'sacs-a-main': { tagline: '50 sacs, 50 silhouettes', description: "Du cabas structuré à la pochette du soir, cinquante variations du geste du sellier." },
  'petite-maroquinerie': { tagline: '50 objets, 50 compagnons', description: "Portefeuilles, porte-cartes, étuis : cinquante pièces qui vous suivent partout." },
  'sacs-bagagerie': { tagline: '50 bagages, 50 voyages', description: "Backpacks, weekender, cabine : cinquante compagnons pour le voyage de votre vie." },
  'ceintures': { tagline: '50 ceintures, 50 allures', description: "Sellier, tressée, réversible : cinquante manières de signaler le caractère." },
  'chaussures': { tagline: '50 paires, 50 démarches', description: "Oxfords, derbys, bottines : cinquante montages Goodyear, autant d'allures." },
  'vestes-pret-a-porter': { tagline: '50 vestes, 50 caractères', description: "Perfecto, blazer, bomber : cinquante interprétations du vêtement de cuir." },
  'manteaux-vestes': { tagline: '50 manteaux, 50 hivers', description: "Trench long, parka, redingote : cinquante pièces pour affronter l'hiver avec style." },
  'pantalons': { tagline: '50 pantalons, 50 coupes', description: "Droit, skinny, jodhpur : cinquante façons d'habiller le bas du corps en cuir." },
  'jupes': { tagline: '50 jupes, 50 élégances', description: "Crayon, évasée, plissée : cinquante silhouettes féminines en cuir." },
  'gilets': { tagline: '50 gilets, 50 superpositions', description: "Tailored, motard, frangé : cinquante couches pour sublimer une tenue." },
  'echarpes-foulards': { tagline: '50 écharpes, 50 détails', description: "Cuir & soie, foulards ajourés : cinquante façons de finir une allure." },
  'nattes-priere': { tagline: '50 nattes, 50 recueillements', description: "Gravées main, brodées fil doré : cinquante nattes pour le recueillement." },
  'coffrets-accessoires': { tagline: '50 coffrets, 50 présents', description: "Gants, bracelets, étuis : cinquante cadeaux de maison par excellence." },
};

export default function GalleryAll() {
  const [collections, setCollections] = useState<CollectionData[]>([]);
  const [active, setActive] = useState('sacs-a-main');
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number; collection: string } | null>(null);

  const fetchCollections = useCallback(async () => {
    try {
      const res = await fetch('/api/gallery', { cache: 'no-store' });
      const data = await res.json();
      setCollections(data.collections);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let intervalRef: ReturnType<typeof setInterval> | null = null;
    const run = async () => {
      await fetchCollections();
      intervalRef = setInterval(async () => {
        if (!cancelled) await fetchCollections();
      }, 30000); // refresh less often now that we have all images
    };
    run();
    return () => {
      cancelled = true;
      if (intervalRef) clearInterval(intervalRef);
    };
  }, [fetchCollections]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowLeft') setLightbox((lb) => lb ? { ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length } : lb);
      if (e.key === 'ArrowRight') setLightbox((lb) => lb ? { ...lb, index: (lb.index + 1) % lb.images.length } : lb);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox]);

  const activeCollection = collections.find((c) => c.id === active);
  const totalEquivalent = collections.reduce((s, c) => s + c.equivalentCount, 0);

  const navLightbox = (dir: 'prev' | 'next') => {
    if (!lightbox) return;
    setLightbox((lb) => lb ? { ...lb, index: dir === 'prev' ? (lb.index - 1 + lb.images.length) % lb.images.length : (lb.index + 1) % lb.images.length } : lb);
  };

  const isWall = (img: string) => img.includes('-wall-');

  return (
    <section id="galerie" className="relative py-24 sm:py-32 bg-[#0a0a0f] border-t border-[#D4AF37]/10 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'url(/images/leather-texture.png)', backgroundSize: 'cover' }} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block h-px w-10 bg-[#D4AF37]" />
            <span className="eyebrow">Galerie Vivante</span>
            <span className="block h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
            <span className="text-[#F4E8C1]">Six cent cinquante pièces,</span>{' '}
            <span className="text-gold-gradient">treize familles.</span>
          </h2>
          <p className="max-w-3xl mx-auto text-[#F4E8C1]/65 font-light leading-relaxed">
            Chaque collection exprime une variation du geste de l'artisan à travers
            cinquante images — un atlas vivant du savoir-faire MATAR STYLE CUIR.
            Treize familles, du sac à la natte de prière, du cuir nappa au tanné végétal.
          </p>

          {!loading && (
            <div className="mt-6 inline-flex items-center gap-3 px-5 py-2.5 bg-[#12121a]/70 border border-[#D4AF37]/25">
              <span className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" />
              <span className="text-[11px] uppercase tracking-[0.25em] text-[#F4E8C1]/70">
                {totalEquivalent} pièces exposées · 13 collections
              </span>
            </div>
          )}
        </div>

        {/* Tab grid — 13 categories */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-2 mb-12">
          {collections.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={`group flex flex-col items-center text-center p-3 border transition-all duration-300 ${
                active === c.id
                  ? 'bg-gold-gradient border-[#D4AF37] text-[#0a0a0f]'
                  : 'bg-transparent border-[#D4AF37]/20 text-[#F4E8C1]/75 hover:border-[#D4AF37]/55 hover:text-[#F4E8C1]'
              }`}
            >
              <span className="text-[10px] uppercase tracking-[0.15em] font-semibold leading-tight">
                {c.name}
              </span>
              <span className={`text-[9px] uppercase tracking-[0.2em] mt-1 ${active === c.id ? 'text-[#0a0a0f]/70' : 'text-[#D4AF37]/70'}`}>
                {c.walls} murs · {c.singles} pièces
              </span>
            </button>
          ))}
        </div>

        {/* Active collection */}
        {activeCollection && (
          <div className="mb-10">
            <div className="text-center mb-10 max-w-3xl mx-auto">
              <p className="font-serif italic text-xl sm:text-2xl text-[#D4AF37] mb-4">
                {COLLECTION_DESC[activeCollection.id]?.tagline}
              </p>
              <p className="text-sm text-[#F4E8C1]/65 leading-relaxed font-light">
                {COLLECTION_DESC[activeCollection.id]?.description}
              </p>
            </div>

            {activeCollection.images.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-[#F4E8C1]/40">
                <ImageIcon size={32} className="text-[#D4AF37] mb-4" />
                <p className="text-xs uppercase tracking-[0.25em]">Aucune image disponible</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 auto-rows-[minmax(120px,_auto)]">
                {activeCollection.images.map((img, idx) => {
                  const wall = isWall(img);
                  return (
                    <button
                      key={img}
                      onClick={() => setLightbox({ collection: activeCollection.id, images: activeCollection.images, index: idx })}
                      className={`group relative overflow-hidden bg-[#12121a] border border-[#D4AF37]/15 hover:border-[#D4AF37]/50 transition-all duration-400 ${
                        wall
                          ? 'col-span-2 row-span-2 sm:col-span-3 sm:row-span-2 lg:col-span-3 lg:row-span-2 aspect-[16/10] sm:aspect-[3/1] lg:aspect-[3/1]'
                          : 'aspect-square'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${activeCollection.name} ${idx + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        style={{ filter: 'brightness(0.9) saturate(1.05)' }}
                      />
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400" style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(10,10,15,0.95) 100%)' }} />
                      <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex items-end justify-between">
                        <div>
                          <div className="text-[9px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">
                            {wall ? "Mur d'inspiration" : `N°${String(idx + 1).padStart(2, '0')}`}
                          </div>
                          {wall && (
                            <div className="text-[10px] text-[#F4E8C1]/70 mt-0.5 italic font-serif">
                              ~9 pièces exposées
                            </div>
                          )}
                        </div>
                        {wall && <span className="text-[#D4AF37] text-xs">✦</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="text-center mt-10">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#F4E8C1]/40">
            Cliquez sur une image pour l'agrandir · Navigatez avec ← →
          </p>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-[#0a0a0f]/97 backdrop-blur-md flex items-center justify-center p-4 sm:p-8" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0f] transition-all z-10" aria-label="Fermer">
            <X size={20} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); navLightbox('prev'); }} className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0f] transition-all z-10" aria-label="Précédent">
            <ChevronLeft size={22} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); navLightbox('next'); }} className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0f] transition-all z-10" aria-label="Suivant">
            <ChevronRight size={22} />
          </button>
          <div className="relative max-w-5xl max-h-[85vh] w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.images[lightbox.index]} alt={`${lightbox.collection} ${lightbox.index + 1}`} className="max-w-full max-h-[78vh] object-contain border-gold-thin" />
            <div className="mt-5 text-center">
              <div className="font-display text-sm uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                {COLLECTION_DESC[lightbox.collection]?.tagline}
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4E8C1]/55 mt-2">
                {isWall(lightbox.images[lightbox.index]) ? "Mur d'inspiration" : `Pièce ${String(lightbox.index + 1).padStart(2, '0')}`} · {String(lightbox.images.length).padStart(2, '0')} · MATAR STYLE CUIR
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
